import { Button, Divider, Flex, Icon, IconButton, Select, Stack } from '@chakra-ui/react';
import { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import { sortBy } from 'lodash';
import { Fragment, useMemo } from 'react';
import { BiPlus, BiX } from 'react-icons/bi';

import { FilterType } from '../../_openapi/portfolio-optimizer';
import { getColumns } from '../../Dashboard/DashboardWidget/widgetUtils';
import { getColumnLabel } from '../../utils/tableUtils';
import { BooleanFilter } from '../filters/BooleanFilter';
import { MultiSelectFilter } from '../filters/MultiSelectFilter';
import { NumberFilter, NumberFilterValue } from '../filters/NumberFilter';
import { TextFilter } from '../filters/TextFilter';
import { WidgetDateFilter, WidgetDateFilterValue } from './WidgetDateFilter';

export const WidgetFilters = ({
    widgetType,
    filters,
    setFilters,
}: {
    widgetType: DashboardWidgetType;
    filters: ColumnFiltersState;
    setFilters: (filters: ColumnFiltersState) => void;
}) => {
    const columnDefinitions = useMemo(() => getColumns(widgetType), [widgetType]);
    const columns = useMemo(() => {
        return sortBy(
            getColumns(widgetType).map((col) => {
                return {
                    id: col.id,
                    label: typeof col.header === 'string' ? col.header : getColumnLabel(col.id!),
                };
            }),
            'label',
        );
    }, [widgetType]);

    return (
        <Stack gap={4}>
            {filters.map((filter, idx) => {
                const columnDef = columnDefinitions.find(({ id }) => id === filter.id);
                return (
                    <Fragment key={idx}>
                        <Flex>
                            <IconButton
                                aria-label="remove-filter"
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                icon={<Icon as={BiX} />}
                                onClick={() => setFilters(filters.filter((_, i) => i !== idx))}
                            />

                            <Flex width="200px" mr={8}>
                                <Select
                                    size="sm"
                                    placeholder="Select column"
                                    value={filter.id}
                                    onChange={(ev) =>
                                        setFilters(
                                            filters.map((columnFilter, i) =>
                                                i === idx ? { id: ev.target.value, value: undefined } : columnFilter,
                                            ),
                                        )
                                    }
                                >
                                    {columns.map((column) => (
                                        <option key={column.id} value={column.id}>
                                            {column.label}
                                        </option>
                                    ))}
                                </Select>
                            </Flex>

                            {columnDef && (
                                <ColumnFilter
                                    columnDef={columnDef}
                                    filterValue={filter.value}
                                    onChange={(value) =>
                                        setFilters(
                                            filters.map((columnFilter, i) =>
                                                i === idx ? { id: columnFilter.id, value } : columnFilter,
                                            ),
                                        )
                                    }
                                />
                            )}
                        </Flex>

                        {idx !== filters.length - 1 && <Divider />}
                    </Fragment>
                );
            })}
            <Flex>
                <Button
                    leftIcon={<Icon as={BiPlus} />}
                    size="sm"
                    onClick={() => setFilters([...filters, { id: '', value: undefined }])}
                >
                    Add filter
                </Button>
            </Flex>
        </Stack>
    );
};

const ColumnFilter = ({
    columnDef,
    onChange,
    filterValue,
}: {
    columnDef: ColumnDef<any, any>;
    onChange: (value: any) => void;
    filterValue: any;
}) => {
    switch (columnDef.meta?.filterType) {
        case FilterType.LIST:
            return (
                <MultiSelectFilter
                    options={columnDef.meta?.filterOptionsFn?.() as string[]}
                    filterValue={filterValue as string[] | undefined}
                    setFilterValue={onChange}
                />
            );
        case FilterType.DATE: {
            return <WidgetDateFilter filterValue={filterValue as WidgetDateFilterValue} setFilterValue={onChange} />;
        }
        case FilterType.NUMBER: {
            return <NumberFilter filterValue={filterValue as NumberFilterValue} setFilterValue={onChange} />;
        }
        case FilterType.BOOLEAN:
            return <BooleanFilter filterValue={filterValue as boolean | undefined} setFilterValue={onChange} />;
        default: {
            // ensure compatibility with old text filter where the value was a plain string instead of an array
            const value = !filterValue || Array.isArray(filterValue) ? filterValue : [filterValue];
            return <TextFilter filterValue={value as string[] | undefined} setFilterValue={onChange} />;
        }
    }
};
